// src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { name: "Home",      path: "/" },
  { name: "Services",  path: "/services" },
  { name: "Prices",    path: "/product" },
  { name: "Blogs",     path: "/blogs" },
  { name: "Portfolio", path: "/PortfolioSection" },
  { name: "About",     path: "/about" },
  { name: "Contact",   path: "/contact" },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen]   = useState(false);
  const [scrolled, setScrolled]   = useState(false);
  const location                  = useLocation();

  /* ── Scroll shadow ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Lock body scroll when drawer is open ── */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  /* ── Close drawer on route change ── */
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <>
      <style>{`
        .nav-link-underline::after {
          content: '';
          display: block;
          position: absolute;
          left: 0; bottom: -2px;
          height: 2px;
          width: 0;
          background: linear-gradient(90deg, #3b82f6, #ec4899, #a855f7);
          border-radius: 99px;
          transition: width 0.35s cubic-bezier(0.4,0,0.2,1);
        }
        .nav-link-underline:hover::after,
        .nav-link-underline.active::after { width: 100%; }

        /* Hamburger bars */
        .bar {
          display: block;
          width: 22px; height: 2px;
          background: #374151;
          border-radius: 2px;
          transition: transform 0.3s ease, opacity 0.3s ease, width 0.3s ease;
          transform-origin: center;
        }
        .bar-top.open    { transform: translateY(8px) rotate(45deg); }
        .bar-mid.open    { opacity: 0; width: 0; }
        .bar-bot.open    { transform: translateY(-8px) rotate(-45deg); }

        /* Drawer slide */
        .drawer {
          transform: translateX(100%);
          transition: transform 0.32s cubic-bezier(0.4,0,0.2,1);
        }
        .drawer.open { transform: translateX(0); }

        /* Backdrop fade */
        .backdrop {
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.32s ease;
        }
        .backdrop.open { opacity: 1; pointer-events: auto; }

        /* Mobile nav item slide-in */
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .mobile-nav-item {
          animation: slideIn 0.35s ease both;
        }
      `}</style>

      {/* ── Top contact strip ── */}
      <div className="bg-gray-800 text-white text-xs sm:text-sm py-1.5 px-4 flex flex-row justify-between items-center">
        <a href="mailto:info@astergo.in" className="hover:text-blue-300 transition-colors truncate mr-2">
          📧 info@astergo.in
        </a>
        <a href="tel:08210345655" className="hover:text-blue-300 transition-colors whitespace-nowrap">
          📞 08210345655 / 9163091507
        </a>
      </div>

      {/* ── Main Navbar ── */}
      <header
        className={`sticky top-0 z-40 bg-white flex items-center justify-between px-4 sm:px-6 md:px-12 py-3 sm:py-4 transition-shadow duration-300 ${
          scrolled ? "shadow-md" : "shadow-sm"
        }`}
      >
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src="/assets/img/astergo-logo.png"
            alt="Astergo Logo"
            className="w-24 sm:w-28 h-auto"
          />
        </Link>

        {/* Desktop Nav */}
        <nav aria-label="Primary navigation" className="hidden md:flex items-center space-x-1 lg:space-x-2">
          {navLinks.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`nav-link-underline relative px-2 lg:px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ${
                isActive(item.path)
                  ? "text-blue-600 active"
                  : "text-gray-700 hover:text-blue-600"
              }`}
            >
              {item.name}
            </Link>
          ))}

          {/* Optional CTA */}
          <Link
            to="/contact"
            className="ml-3 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-semibold shadow hover:shadow-md hover:opacity-90 transition-all duration-200"
          >
            Get Started
          </Link>
        </nav>

        {/* Hamburger Button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          className="md:hidden flex flex-col justify-center items-center gap-1.5 w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <span className={`bar bar-top ${menuOpen ? "open" : ""}`} />
          <span className={`bar bar-mid ${menuOpen ? "open" : ""}`} />
          <span className={`bar bar-bot ${menuOpen ? "open" : ""}`} />
        </button>
      </header>

      {/* ── Backdrop ── */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm ${menuOpen ? "backdrop open" : "backdrop"}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      {/* ── Mobile Drawer ── */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        className={`drawer fixed top-0 right-0 z-50 h-full w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col ${
          menuOpen ? "open" : ""
        }`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <img
            src="/assets/img/astergo-logo.png"
            alt="Astergo Logo"
            className="w-24 h-auto"
          />
          <button
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800"
          >
            {/* X icon */}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Drawer Nav Links */}
        <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {navLinks.map((item, i) => (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setMenuOpen(false)}
              style={{ animationDelay: menuOpen ? `${i * 45}ms` : "0ms" }}
              className={`mobile-nav-item flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors duration-200 ${
                isActive(item.path)
                  ? "bg-blue-50 text-blue-600"
                  : "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
              }`}
            >
              {/* Active dot */}
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${
                  isActive(item.path) ? "bg-blue-500" : "bg-transparent"
                }`}
              />
              {item.name}
            </Link>
          ))}
        </nav>

        {/* Drawer Footer CTA */}
        <div className="px-4 py-5 border-t border-gray-100 space-y-3">
          <Link
            to="/contact"
            onClick={() => setMenuOpen(false)}
            className="block w-full text-center px-4 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition-opacity"
          >
            Get Started
          </Link>

          <div className="flex flex-col gap-1 text-center">
            <a href="mailto:info@astergo.in" className="text-xs text-gray-500 hover:text-blue-600 transition-colors">
              📧 info@astergo.in
            </a>
            <a href="tel:08210345655" className="text-xs text-gray-500 hover:text-blue-600 transition-colors">
              📞 08210345655
            </a>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Navbar;